import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobsOnboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState(searchParams.get('type') || 'job_seeker');

  // Redirect if already has jobs profile
  React.useEffect(() => {
    if (user?.has_jobs_profile) {
      navigate('/jobs/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/profile/enable-jobs`, { user_type: userType });
      toast.success('GoJobs profile enabled successfully!');
      
      // Refresh user data
      await axios.get(`${API}/auth/me`);
      
      navigate('/jobs/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable GoJobs profile');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Please log in to continue</h2>
          <button
            onClick={() => navigate('/login?redirect=/jobs/onboarding')}
            className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 dark:from-gray-900 dark:to-gray-800 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-card rounded-2xl shadow-xl p-8 md:p-12 border">
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold mb-3">Enable GoJobs</h1>
              <p className="text-muted-foreground text-lg">
                Set up your jobs profile to start your journey
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-3">
                  I want to <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setUserType('job_seeker')}
                    className={`px-6 py-6 border-2 rounded-xl font-medium transition-all text-left ${
                      userType === 'job_seeker'
                        ? 'border-purple-600 bg-purple-50 text-purple-600'
                        : 'border-gray-300 hover:border-purple-300'
                    }`}
                  >
                    <div className="text-lg font-bold mb-2">Find a Job</div>
                    <div className="text-sm opacity-80">
                      I'm looking for employment opportunities
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setUserType('employer')}
                    className={`px-6 py-6 border-2 rounded-xl font-medium transition-all text-left ${
                      userType === 'employer'
                        ? 'border-purple-600 bg-purple-50 text-purple-600'
                        : 'border-gray-300 hover:border-purple-300'
                    }`}
                  >
                    <div className="text-lg font-bold mb-2">Hire Talent</div>
                    <div className="text-sm opacity-80">
                      I want to post jobs and find candidates
                    </div>
                  </button>
                </div>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
                <h3 className="font-semibold mb-2">
                  {userType === 'job_seeker' ? 'As a Job Seeker' : 'As an Employer'}
                </h3>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {userType === 'job_seeker' ? (
                    <>
                      <li>✓ Create your professional profile</li>
                      <li>✓ Upload your resume</li>
                      <li>✓ Apply to jobs with one click</li>
                      <li>✓ Track your applications</li>
                    </>
                  ) : (
                    <>
                      <li>✓ Post unlimited job listings</li>
                      <li>✓ Access qualified candidates</li>
                      <li>✓ Manage applications efficiently</li>
                      <li>✓ Build your employer brand</li>
                    </>
                  )}
                </ul>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-600 text-white font-semibold py-4 rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Enabling...
                  </>
                ) : (
                  'Enable GoJobs Profile'
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate('/jobs')}
                className="w-full px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Back to GoJobs
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobsOnboarding;
